import { useState } from "react";
import { ApiError } from "../api/client";
import { usePromoteCheck, usePromoteMutation } from "../api/queries";
import type { CheckLevel, FleetOverview, PromoteCheck, TreeResponse } from "../api/types";
import Modal from "../components/Modal";
import VersionChip from "../components/VersionChip";
import { showToast } from "../util/toast";
import { newerTags } from "../util/versions";

const CHECK_ICON: Record<CheckLevel, string> = { ok: "✓", warning: "⚠", block: "✗" };

export default function PromoteDialog({
    envKey,
    tree,
    overview,
    onClose,
}: {
    envKey: string;
    tree: TreeResponse;
    overview: FleetOverview;
    onClose: () => void;
}) {
    const tags = overview.latestTags?.components["argocd-ts-cmp"] ?? [];
    const current = tree.pins[envKey]?.version ?? null;
    const newer = newerTags(tags, current);

    const [showAll, setShowAll] = useState(false);
    const [toVersion, setToVersion] = useState<string>(newer[0] ?? "");
    const [understood, setUnderstood] = useState(false);
    // Checks returned by a 409 on submit override the pre-flight ones until the version changes.
    const [conflictChecks, setConflictChecks] = useState<PromoteCheck[] | null>(null);

    const checkQ = usePromoteCheck(envKey, toVersion === "" ? null : toVersion);
    const promote = usePromoteMutation();

    const checks = conflictChecks ?? checkQ.data?.checks ?? [];
    const hasBlock = checks.some((c) => c.level === "block");
    const hasWarning = checks.some((c) => c.level === "warning");
    const canSubmit = !checkQ.isPending && !checkQ.isError && !hasBlock && (!hasWarning || understood) && !promote.isPending;

    const options = showAll ? tags : newer;

    const selectVersion = (v: string) => {
        setToVersion(v);
        setUnderstood(false);
        setConflictChecks(null);
    };

    const submit = () => {
        promote.mutate(
            { envKey, ...(toVersion !== "" ? { toVersion } : {}) },
            {
                onSuccess: (pr) => {
                    const url = pr.mr?.webUrl ?? pr.pipeline?.webUrl;
                    showToast({
                        kind: "success",
                        message: `Promotion of ${envKey} to ${pr.toVersion ?? pr.requestedVersion ?? "upstream version"} requested`,
                        link: url ? { url, label: pr.mr ? `MR !${pr.mr.iid}` : `pipeline #${pr.pipeline?.id ?? "?"}` } : undefined,
                    });
                    onClose();
                },
                onError: (err) => {
                    if (err instanceof ApiError && err.status === 409) {
                        const body = err.body as { checks?: PromoteCheck[] } | null;
                        if (body?.checks) {
                            setConflictChecks(body.checks);
                            setUnderstood(false);
                            return;
                        }
                    }
                    showToast({ kind: "error", message: `Promotion failed: ${err instanceof Error ? err.message : String(err)}` });
                },
            },
        );
    };

    return (
        <Modal title={`Promote ${envKey}`} onClose={onClose}>
            <div className="dialog-row">
                <span className="muted">current pin</span>
                <VersionChip version={current} />
            </div>

            <label className="field">
                Target version
                <select value={toVersion} onChange={(e) => selectVersion(e.target.value)}>
                    <option value="">
                        from upstream{!toVersion && checkQ.data?.resolvedVersion ? ` (${checkQ.data.resolvedVersion})` : ""}
                    </option>
                    {options.map((t) => (
                        <option key={t} value={t}>
                            {t}
                        </option>
                    ))}
                </select>
            </label>
            <label className="checkbox">
                <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />
                show all tags (incl. older than current)
            </label>

            <div className="checks-panel">
                {checkQ.isPending ? <span className="muted">Running checks…</span> : null}
                {checkQ.isError ? <div className="check-row block">✗ checks unavailable: {checkQ.error.message}</div> : null}
                {checks.map((c) => (
                    <div key={c.id} className={`check-row ${c.level}`}>
                        {CHECK_ICON[c.level]} {c.message}
                    </div>
                ))}
            </div>

            {checkQ.data ? (
                <p className="muted policy-line">
                    {checkQ.data.mergePolicy === "delayed"
                        ? "delayed: merges after veto window unless held"
                        : checkQ.data.mergePolicy === "auto"
                          ? "auto: merges as soon as the MR pipeline passes"
                          : checkQ.data.mergePolicy === "manual"
                            ? "manual: MR is opened but left for a human to merge (never merges on its own)"
                            : "merge policy unknown (promotion config not loaded)"}
                    {checkQ.data.fromVersion ? ` · ${checkQ.data.fromVersion} → ${checkQ.data.resolvedVersion ?? toVersion}` : ""}
                </p>
            ) : null}

            {hasWarning ? (
                <label className="checkbox warning">
                    <input type="checkbox" checked={understood} onChange={(e) => setUnderstood(e.target.checked)} />I understand the
                    warnings above
                </label>
            ) : null}

            <div className="dialog-actions">
                <button className="btn" onClick={onClose}>
                    Cancel
                </button>
                <button className="btn btn-primary" disabled={!canSubmit} onClick={submit}>
                    {promote.isPending ? "Submitting…" : "Promote"}
                </button>
            </div>
        </Modal>
    );
}
